import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, KeyRound, Pencil, RotateCcw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InviteBadge } from "@/components/InviteBadge";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const IconBtn = ({ label, onClick, danger, testId, children }) => (
  <button type="button" onClick={onClick} title={label} aria-label={label} data-testid={testId}
    className={`p-2 rounded-md transition-colors text-slate-500 ${danger ? "hover:text-red-600 hover:bg-red-50" : "hover:text-sky-700 hover:bg-sky-50"}`}>{children}</button>
);

const CONFIRM = {
  delete: { title: "Slette brukeren?", text: "Brukeren logges ut og kan ikke logge inn lenger. Du kan gjenopprette brukeren senere fra denne listen.", btn: "Slett bruker", danger: true },
  reset: { title: "Sende lenke for nytt passord?", text: "Brukeren får en e-post med en lenke som er gyldig i 1 time, og velger passordet selv. Det gamle passordet virker til et nytt er valgt.", btn: "Send lenke" },
  restore: { title: "Gjenopprette brukeren?", text: "Brukeren kan logge inn igjen med sitt gamle passord.", btn: "Gjenopprett" },
};

const EditDialog = ({ u, onClose, onSaved }) => {
  const [f, setF] = useState({ name: u?.name || "", email: u?.email || "" });
  const save = async () => {
    try {
      await api.patch(`/admin/users/${u.id}`, f);
      toast.success("Brukeren er oppdatert");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!u} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-edit-dialog" onOpenAutoFocus={() => setF({ name: u?.name || "", email: u?.email || "" })}>
        <DialogHeader><DialogTitle>Rediger bruker</DialogTitle><DialogDescription>{u?.customer_name}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm font-medium text-slate-700">Navn<Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-edit-name" /></label>
          <label className="block text-sm font-medium text-slate-700">E-post<Input className="mt-1" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-edit-email" /></label>
          {f.email.trim().toLowerCase() !== (u?.email || "") && <p className="text-xs text-amber-700">Når e-posten endres, logges brukeren ut og må logge inn med den nye adressen.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="user-edit-cancel">Avbryt</Button>
          <Button onClick={save} disabled={!f.email.trim()} data-testid="user-edit-save">Lagre</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserLine = ({ u, onAct }) => (
  <li className={`flex flex-col sm:flex-row sm:items-center gap-2 justify-between px-4 py-3 ${u.deleted ? "bg-slate-50" : ""}`} data-testid={`all-user-${u.id}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className={`font-medium truncate ${u.deleted ? "text-slate-400 line-through" : "text-slate-900"}`}>{u.name || u.email}</span>
        {u.deleted && <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium" data-testid={`all-user-${u.id}-deleted`}>Slettet {fmtDate(u.deleted_at)}</span>}
      </div>
      <div className="text-xs text-slate-500 truncate">{u.email} · Sist innlogget {fmtDate(u.last_login)}</div>
      {!u.deleted && u.invite_pending && <div className="mt-1"><InviteBadge url={`/admin/users/${u.id}/invite`} testId={`all-user-${u.id}-invite`} /></div>}
    </div>
    <div className="flex shrink-0">
      {u.deleted ? (
        <IconBtn label="Gjenopprett" onClick={() => onAct("restore", u)} testId={`all-user-${u.id}-restore`}><RotateCcw className="w-4 h-4" /></IconBtn>
      ) : (<>
        <IconBtn label="Rediger" onClick={() => onAct("edit", u)} testId={`all-user-${u.id}-edit`}><Pencil className="w-4 h-4" /></IconBtn>
        {!u.invite_pending && <IconBtn label="Send lenke for nytt passord" onClick={() => onAct("reset", u)} testId={`all-user-${u.id}-reset`}><KeyRound className="w-4 h-4" /></IconBtn>}
        <IconBtn label="Slett" danger onClick={() => onAct("delete", u)} testId={`all-user-${u.id}-delete`}><Trash2 className="w-4 h-4" /></IconBtn>
      </>)}
    </div>
  </li>
);

const Group = ({ name, users, onAct }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid={`user-group-${name}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="font-medium text-slate-900">{name} <span className="text-sm text-slate-500 font-normal">· {users.filter((u) => !u.deleted).length} brukere</span></span>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <ul className="divide-y divide-slate-100">{users.map((u) => <UserLine key={u.id} u={u} onAct={onAct} />)}</ul>}
    </div>
  );
};

export const UsersSection = ({ refreshKey, onChanged }) => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      setUsers((await api.get("/admin/users")).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const m = {};
    users.filter((u) => (showDeleted || !u.deleted) && (!s || [u.name, u.email, u.customer_name].some((x) => (x || "").toLowerCase().includes(s))))
      .forEach((u) => { (m[u.customer_name] = m[u.customer_name] || []).push(u); });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b, "nb"));
  }, [users, q, showDeleted]);

  const done = () => { setEdit(null); setConfirm(null); load(); onChanged(); };
  const run = async () => {
    const { kind, u } = confirm;
    try {
      if (kind === "delete") await api.delete(`/admin/users/${u.id}`);
      if (kind === "restore") await api.post(`/admin/users/${u.id}/restore`);
      if (kind === "reset") await api.post(`/admin/users/${u.id}/reset-link`);
      toast.success(kind === "delete" ? "Brukeren er slettet" : kind === "restore" ? "Brukeren er gjenopprettet" : `Lenke for nytt passord er sendt til ${u.email}`);
      done();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const c = confirm && CONFIRM[confirm.kind];

  return (
    <div className="space-y-4" data-testid="users-section">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="relative sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk etter navn, e-post eller kunde" className="pl-9 bg-white" data-testid="users-search-input" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <Switch checked={showDeleted} onCheckedChange={setShowDeleted} data-testid="users-show-deleted-switch" />Vis slettede brukere
        </label>
      </div>
      {groups.map(([name, us]) => <Group key={name} name={name} users={us} onAct={(kind, u) => (kind === "edit" ? setEdit(u) : setConfirm({ kind, u }))} />)}
      {!groups.length && <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-lg p-6 text-center">Ingen brukere funnet.</p>}
      <EditDialog u={edit} onClose={() => setEdit(null)} onSaved={done} />
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="user-confirm-dialog">
          <DialogHeader><DialogTitle>{c?.title}</DialogTitle><DialogDescription>{confirm?.u.email} – {c?.text}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="user-confirm-cancel">Avbryt</Button>
            <Button variant={c?.danger ? "destructive" : "default"} onClick={run} data-testid="user-confirm-ok">{c?.btn}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
